<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-sky-200 via-teal-200 to-emerald-200 overflow-hidden">
      <div class="absolute inset-0 bg-white/30 backdrop-blur-sm"></div>
      <div class="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl py-12 sm:py-16 lg:py-20">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <!-- Left Content -->
          <div class="text-gray-800 lg:pr-12">
            <h1 class="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              AI <span class="text-yellow-500">智慧e生</span>校務管理軟體
            </h1>
            <p class="text-base sm:text-lg lg:text-xl mb-8 text-gray-700 leading-relaxed max-w-xl">
              整合學校行政、教學管理、學生資訊等多元功能，透過AI演算法與數據分析，協助學校提升管理效率與決策品質。
            </p>
          </div>

          <!-- Right Image -->
          <div class="flex justify-center lg:justify-end">
            <div class="relative w-full max-w-xl">
              <img src="@/assets/img/product2-0.jpg" alt="AI智慧e生校務管理軟體"
                class="rounded-3xl shadow-2xl object-cover w-full h-auto" />
              <!-- Decorative Circles -->
              <div class="absolute -top-4 -right-4 w-20 h-20 bg-yellow-300 rounded-full opacity-20"></div>
              <div class="absolute -bottom-4 -left-4 w-16 h-16 bg-teal-300 rounded-full opacity-20"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Decorative Elements -->
      <div class="absolute top-0 right-0 w-1/3 h-full opacity-10">
        <div class="w-full h-full bg-gradient-to-l from-white to-transparent"></div>
      </div>
    </section>

    <!-- 核心功能介紹區 -->
    <section class="py-12 bg-white">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-12 text-center">
          核心功能介紹
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-sky-600 mb-2">資訊公告</h3>
            <p class="text-gray-600">活動資訊、招生資訊、促銷資訊、重大資訊發布，家長及老師皆可從手機即時接收學校的即時通知公告。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-emerald-600 mb-2">簽到記錄</h3>
            <p class="text-gray-600">上課前一天通知提醒、孩子到校手機報到、超時遲到提醒、延遲下課通知等完整簽到管理。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-yellow-600 mb-2">訊息提醒</h3>
            <p class="text-gray-600">上課提醒、遲到提醒、缺課通知、簽到通知、生日祝福、審核提醒等多樣推播訊息。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-teal-600 mb-2">作業通知</h3>
            <p class="text-gray-600">老師課後佈置作業並發送給學生，學生完成回覆後可獲得評語及回饋，支援圖文影音格式。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-indigo-600 mb-2">課程回顧</h3>
            <p class="text-gray-600">每堂課後老師分享課堂亮點與學生表現，家長與學生可瀏覽並轉發至朋友圈。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-pink-600 mb-2">考試成績</h3>
            <p class="text-gray-600">學校可查看每個班級的在校成績和訓練機構成績，學生可查看個人成績記錄。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-purple-600 mb-2">積分管理</h3>
            <p class="text-gray-600">支持積分累積與兌換，分享小程序、完成作業等可獲得積分，兌換贈品或抵扣課時費。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-red-600 mb-2">微信支付</h3>
            <p class="text-gray-600">支援手機線上報名及微信支付訂單費用，家長可查看歷史訂單及支付記錄。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-orange-600 mb-2">上課回饋</h3>
            <p class="text-gray-600">學生可對老師教學表現評估，老師可對學生學習表現評價並推播學習指導方案。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-cyan-600 mb-2">課程費用</h3>
            <p class="text-gray-600">學校可查看每個班級/學生的學費及課程，學生可查看個人報名課程的學費及課時。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-lime-600 mb-2">排課表</h3>
            <p class="text-gray-600">校方可查看每位老師/教室的排課表，家長可查看孩子的排課表並接受上課提醒。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-amber-600 mb-2">報表分析</h3>
            <p class="text-gray-600">依校方營運資料進行統計分析，包含招生動態、學員分佈、報名意願、財務統計等。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-violet-600 mb-2">上課點名</h3>
            <p class="text-gray-600">老師手機點名及考勤列表，可查詢每個班級所有出勤狀況，提升課堂管理效率。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-rose-600 mb-2">招生裂變</h3>
            <p class="text-gray-600">透過激勵方式讓會員分享，支持積分獎勵與多階層次推廣，提升招生效果。</p>
          </div>
          <div class="bg-gray-50 p-6 rounded-xl shadow hover:shadow-md transition">
            <h3 class="text-xl font-semibold text-blue-600 mb-2">線上報名</h3>
            <p class="text-gray-600">支援團班課、活動促銷課、會員優惠課時包、免費試聽課等多元線上報名與支付。</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 介面導覽區 -->
    <section class="py-12 bg-gray-50">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-12 text-center">
          介面導覽
        </h2>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          <div v-for="(item, index) in featureList" :key="index" class="text-center space-y-4">
            <div>
              <h3 :class="'text-2xl font-semibold mb-2 ' + item.color">{{ item.title }}</h3>
              <p class="text-gray-700 text-base px-2">{{ item.description }}</p>
            </div>
            <div class="cursor-pointer inline-block" @click="showLightbox(index)">
              <img :src="item.img" :alt="item.title"
                class="rounded-xl shadow-md w-full max-w-xs mx-auto transition hover:scale-105 duration-200" />
            </div>
          </div>
        </div>

        <!-- 圖片燈箱 -->
        <vue-easy-lightbox :visible="lightboxVisible" :imgs="featureList.map(item => item.img)" :index="lightboxIndex"
          @hide="lightboxVisible = false" />
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-12 -mb-3 bg-white">
      <div class="container mx-auto px-6 text-center">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-4xl font-bold text-gray-800 mb-6">
            立即體驗AI智慧e生校務管理軟體
          </h2>
          <p class="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            專業團隊提供完整的產品諮詢與技術支援，協助您快速部署
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <router-link to="/contactus">
              <button aria-label="聯絡我們瞭解更多"
                class="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
                聯絡我們瞭解更多
              </button>
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import VueEasyLightbox from 'vue-easy-lightbox'
import img1 from '@/assets/img/product2-1.jpg'
import img2 from '@/assets/img/product2-1.jpg'
import img3 from '@/assets/img/product2-1.jpg'
import img4 from '@/assets/img/product2-1.jpg'
import img5 from '@/assets/img/product2-1.jpg'
import img6 from '@/assets/img/product2-1.jpg'
// 新增的圖片引用，你需要準備這些圖片檔案
import img7 from '@/assets/img/product2-1.jpg'
import img8 from '@/assets/img/product2-8.jpg'
import img9 from '@/assets/img/product2-1.jpg'
import img10 from '@/assets/img/product2-1.jpg'
import img11 from '@/assets/img/product2-1.jpg'
import img12 from '@/assets/img/product2-1.jpg'
import img13 from '@/assets/img/product2-1.jpg'
import img14 from '@/assets/img/product2-1.jpg'
import img15 from '@/assets/img/product2-15.jpg'


const lightboxVisible = ref(false)
const lightboxIndex = ref(0)

const showLightbox = (index) => {
  lightboxIndex.value = index
  lightboxVisible.value = true
}

const featureList = [
  {
    title: '資訊公告',
    img: img1,
    color: 'text-sky-600',
  },
  {
    title: '簽到記錄',
    img: img2,
    color: 'text-emerald-600',
  },
  {
    title: '訊息提醒',
    img: img3,
    color: 'text-yellow-600',
  },
  {
    title: '作業通知',
    img: img4,
    color: 'text-teal-600',
  },
  {
    title: '課程回顧',
    img: img5,
    color: 'text-indigo-600',
  },
  {
    title: '考試成績',
    img: img6,
    color: 'text-pink-600',
  },
  {
    title: '積分管理',
    img: img7,
    color: 'text-purple-600',
  },
  {
    title: '微信支付',
    img: img8,
    color: 'text-red-600',
  },
  {
    title: '上課回饋',
    img: img9,
    color: 'text-orange-600',
  },
  {
    title: '課程費用',
    img: img10,
    color: 'text-cyan-600',
  },
  {
    title: '排課表',
    img: img11,
    color: 'text-lime-600',
  },
  {
    title: '報表分析',
    img: img12,
    color: 'text-amber-600',
  },
  {
    title: '上課點名',
    img: img13,
    color: 'text-violet-600',
  },
  {
    title: '招生裂變',
    img: img14,
    color: 'text-rose-600',
  },
  {
    title: '線上報名',
    img: img15,
    color: 'text-blue-600',
  }
]

</script>

<style lang="scss" scoped>
// 修正 z-index 層級問題
.text-gray-800 {
  position: relative;
  z-index: 1;
}

// Hero section 的背景層級
.absolute.inset-0 {
  z-index: 0;
}

// 裝飾元素層級
.absolute.top-0.right-0 {
  z-index: 0;
}

section.bg-white {
  position: relative;
  z-index: 1;
}

// 卡片 hover 效果
.group:hover .w-6.h-6 {
  transform: scale(1.1);
}

// 響應式設計
@media (max-width: 768px) {
  .grid.md\\:grid-cols-2 {
    grid-template-columns: 1fr;
  }

  .grid.md\\:grid-cols-3 {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .text-3xl.sm\\:text-4xl.lg\\:text-6xl {
    font-size: 2rem;
  }

  .text-base.sm\\:text-lg.lg\\:text-xl {
    font-size: 1rem;
  }
}

// 動畫效果
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.6s ease-out;
}

// 按鈕 hover 效果
button {
  transition: all 0.3s ease;
}

button:hover {
  transform: translateY(-2px);
}

// 響應式按鈕
@media (max-width: 640px) {
  .flex.flex-col.sm\\:flex-row {
    flex-direction: column;
  }

  button {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}

// 圖片容器樣式
img {
  transition: transform 0.3s ease;
}

img:hover {
  transform: scale(1.02);
}

// 功能卡片 hover 效果
.flex.items-start.gap-4 {
  transition: all 0.3s ease;
  padding: 1rem;
  border-radius: 1rem;
}

.flex.items-start.gap-4:hover {
  background-color: rgba(59, 130, 246, 0.05);
  transform: translateY(-2px);
}

// 漸層背景
.bg-gradient-to-br.from-sky-50.to-emerald-50 {
  background: linear-gradient(135deg, #f0f9ff 0%, #ecfdf5 100%);
}
</style>